import React, { useState, useEffect, useCallback } from 'react';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import PropTypes from 'prop-types';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
// Models
import { InventoryEntities } from 'client/data/models/inventory';
// Utils
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { modalMousedownTrackingFix } from 'site-modules/shared/utils/modal-tracking-fix';
import { isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
// Constants
import { TrackingConstant } from 'client/tracking/constant';
import {
  CREATIVE_ID,
  INSTALLED_UPGRADES,
  FEATURES,
} from 'client/site-modules/shared/constants/inventory/features-and-specs';
// Components
import { ToggleButton } from 'site-modules/shared/components/inventory/features-modal/toggle-button/toggle-button';
import { OptionDescription } from 'site-modules/inventory/components/options-and-packages/option-description';

export function FeaturesModalView({
  vinData = {},
  isModalOpen = false,
  toggleModal = null,
  isMobile = false,
  defaultDisplay = FEATURES,
  packageValue = 0,
  orderedOptions = [],
  featuresToDisplay = [],
  showToggleBtns = false,
  hideFeatures = false,
}) {
  const [currentDisplay, setDisplay] = useState(hideFeatures ? INSTALLED_UPGRADES : FEATURES);
  const isFeaturesOpened = currentDisplay === FEATURES;
  const isUpgradesOpened = currentDisplay === INSTALLED_UPGRADES;

  const prevIsModalOpen = usePrevious(isModalOpen);
  if (!prevIsModalOpen && isModalOpen && showToggleBtns) {
    setDisplay(defaultDisplay);
  }

  useEffect(() => {
    if (isModalOpen) {
      const pageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
          total_page_height: pageHeight,
          creative_id: CREATIVE_ID,
        },
      });
    }
  }, [isModalOpen]);

  const toggleDisplay = useCallback(
    name => () => {
      setDisplay(name);
    },
    []
  );

  const ariaLabelledBy = showToggleBtns ? `tab-${isFeaturesOpened ? FEATURES : INSTALLED_UPGRADES}` : undefined;
  const role = showToggleBtns ? 'tabpanel' : undefined;
  const isPrivatePartyListing = isPrivateParty(vinData);

  return (
    <Modal
      modalClassName="features-modal"
      contentClassName="modal-content"
      isOpen={isModalOpen}
      toggle={toggleModal}
      labelledBy="features-list-modal"
      data-tracking-parent={CREATIVE_ID}
    >
      <ModalBody
        className="pt-1_5 pb-1 px-1 d-flex flex-column"
        data-testid="modal-body"
        onMouseDown={modalMousedownTrackingFix}
      >
        <h2 id="features-list-modal" className="mb-0_5 size-20 fw-bold text-cool-gray-10">
          All Features
        </h2>
        <div className="medium mb-1">
          {isPrivatePartyListing ? 'As reported by owner.' : 'Contact dealer to confirm.'}
        </div>
        <button
          className="modal-close-button p-0 btn btn-link"
          onClick={toggleModal}
          data-testid="toggle-button"
          aria-label="Close dialog"
        >
          <i className="icon-cross3 close-icon" aria-hidden />
        </button>
        <div className="features-modal-list d-flex flex-column">
          {showToggleBtns && (
            <div className="toggle-btn-group bg-cool-gray-100 mb-1 d-inline-flex align-self-md-start" role="tablist">
              <ToggleButton
                onClick={toggleDisplay(FEATURES)}
                name={FEATURES}
                value={FEATURES}
                isOpen={isFeaturesOpened}
                isMobile={isMobile}
                className="toggle-btn-group-tab"
              />
              <ToggleButton
                onClick={toggleDisplay(INSTALLED_UPGRADES)}
                name={INSTALLED_UPGRADES}
                value={INSTALLED_UPGRADES}
                isOpen={isUpgradesOpened}
                isMobile={isMobile}
                className="toggle-btn-group-tab"
              />
            </div>
          )}
          {isUpgradesOpened && (
            <div className="medium mb-0_75">
              These are factory options & packages that are already installed on this vehicle.
            </div>
          )}
          {isFeaturesOpened && (
            <div className="display-wrapper pt-1 px-1 h-100" role={role} aria-labelledby={ariaLabelledBy}>
              <ul className="options medium m-0 ps-1">
                {featuresToDisplay.map(item => (
                  <li key={item} className="pb-1 px-0_5">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isUpgradesOpened && (
            <div className="display-wrapper pt-1 px-1 h-100" role={role} aria-labelledby={ariaLabelledBy}>
              <ul className="m-0 list-unstyled">
                <li className="mb-1 d-flex justify-content-between">
                  <div className="heading-5 col-auto">Installed Upgrades</div>
                  {packageValue > 0 && (
                    <div className="text-green-50 text-end fw-bold ms-0_5">
                      {formatPriceStringV2(packageValue)}{' '}
                      <span className="text-nowrap">{isNew(vinData.type) ? 'total value' : 'original value'}</span>
                    </div>
                  )}
                </li>
                {orderedOptions.map(({ name, price, id }) => (
                  <Row tag="li" key={id} className="mb-1 m-0 d-flex justify-content-between medium">
                    <Col className="fw-bold ps-0">{name}</Col>
                    {packageValue > 0 && (
                      <Col xs="auto" className="fw-bold ms-0_5 p-0">
                        {formatPriceStringV2(price)}
                      </Col>
                    )}
                    {!isPrivatePartyListing && (
                      <Col xs={12} className="p-0">
                        <OptionDescription vin={vinData.vin} optionId={id} isFeaturesModal />
                      </Col>
                    )}
                  </Row>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}

FeaturesModalView.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isModalOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  isMobile: PropTypes.bool,
  defaultDisplay: PropTypes.string,
  packageValue: PropTypes.number,
  orderedOptions: PropTypes.arrayOf(PropTypes.shape({})),
  featuresToDisplay: PropTypes.arrayOf(PropTypes.string),
  showToggleBtns: PropTypes.bool,
  hideFeatures: PropTypes.bool,
};
